import { useSnapshot } from 'valtio'
import {appState, userState} from '../store'
import React, {useEffect, useState} from 'react'
import useApi from '../hooks/useApi'
import {AiFillSave} from "react-icons/ai";
import {AddMatch, ApiError, Team} from "../@backend";
import { Btn } from '../ui/ds/Btn';
import { Badge } from '../ui/ds/Badge';
import { Input } from '../ui/ds/Input';

export default function AddMatchModal() {
  const appSnap = useSnapshot(appState);
  const userSnap = useSnapshot(userState);

  const { backend } = useApi(true);

  const [teams, setTeams] = useState<Team[]>([] as Team[]);
  const [homeTeam, setHomeTeam] = useState(0);
  const [awayTeam, setAwayTeam] = useState(0);

  const [matchTime, setMatchTime] = useState<string>('');

  const [formErrors, setFormErrors] = useState<string[]>([]);

  useEffect(() => {

    if(userSnap.isLoggedIn && appSnap.modals.showAddMatchModal) {
      backend.teams.teamsControllerGetAllTeams(Number(process.env.REACT_APP_ACTIVE_TOURNAMENT_ID)).then((res) => {
        setTeams(res);
      });
    }
  // `backend` is intentionally omitted because useApi creates a fresh client on each render.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userSnap.isLoggedIn, appSnap.modals.showAddMatchModal]);


  const close = () => {
    appState.modals.showAddMatchModal = false;
  };

  const handleHomeTeam = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setHomeTeam(Number(e.target.value || 0));
  };

  const handleAwayTeam = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setAwayTeam(Number(e.target.value || 0));
  };

  const handleMatchTime = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMatchTime(e.target.value ? new Date(e.target.value).toISOString() : '');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setFormErrors([]);

      if(homeTeam === 0 || awayTeam === 0) {
        setFormErrors(['Vyberte týmy']);
        return;
      }
      if(matchTime === '') {
        setFormErrors(['Vyberte čas zápasu']);
        return;
      }

      const newMatch = {
        teamHomeId: homeTeam,
        teamAwayId: awayTeam,
        matchTime: matchTime,
        tournamentId: Number(process.env.REACT_APP_ACTIVE_TOURNAMENT_ID),
      } as AddMatch;


      await backend.matches.matchControllerAddMatch(newMatch);
      appState.modals.showAddMatchModal = false;
      appState.matches = await backend.matches.matchControllerGetAllMatchesPersonalized(Number(process.env.REACT_APP_ACTIVE_TOURNAMENT_ID));
      setHomeTeam(0);
      setAwayTeam(0);
      setMatchTime('');
    } catch (e: any) {
      if(e instanceof ApiError) {
        setFormErrors([e.body?.error?.message ?? 'Problém s uložením zápasu']);
        return;
      }
      setFormErrors(['Zápas se nepodařilo uložit. Zkuste to prosím znovu.']);
    }

  };

  if(!appSnap.modals.showAddMatchModal) return null;

  const selectClasses = [
    'w-full h-[50px] bg-bg-input border-2 border-border-neutral rounded-input',
    'px-4 text-[16px] font-semibold text-text-primary font-nunito',
    'focus:border-brand-blue focus:outline-none transition-colors duration-150',
  ].join(' ');

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4 py-8 overflow-y-auto"
      onClick={close}
    >
      <div
        className="bg-bg-card rounded-hero shadow-card w-full max-w-lg overflow-hidden my-auto"
        onClick={e => e.stopPropagation()}
      >
        <div className="bg-gradient-header px-6 py-5">
          <h2 className="tb-h2 text-white">Přidat zápas</h2>
          <p className="tb-caption text-white/70">Vyberte týmy a čas začátku zápasu</p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-6 flex flex-col gap-5">
          {formErrors.length > 0 && (
            <div
              className="flex items-center gap-3 bg-semantic-danger/10 border border-semantic-danger/20 rounded-content px-4 py-3"
              role="alert"
            >
              <Badge variant="danger">Chyba</Badge>
              <span className="tb-body text-semantic-danger">{formErrors.join(', ')}</span>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] gap-3 sm:items-end">
            <div className="flex flex-col gap-1.5">
              <label htmlFor="add-match-home-team" className="tb-label">Domácí tým</label>
              <select
                id="add-match-home-team"
                value={homeTeam || ''}
                onChange={handleHomeTeam}
                className={selectClasses}
              >
                <option value="">Vyberte domácí tým</option>
                {teams.map((team, index) => (
                  <option key={team.id ?? index} value={team.id}>
                    {team.nameCountry}
                  </option>
                ))}
              </select>
            </div>

            <div className="tb-micro text-text-muted text-center pb-4">VS</div>

            <div className="flex flex-col gap-1.5">
              <label htmlFor="add-match-away-team" className="tb-label">Venkovní tým</label>
              <select
                id="add-match-away-team"
                value={awayTeam || ''}
                onChange={handleAwayTeam}
                className={selectClasses}
              >
                <option value="">Vyberte venkovní tým</option>
                {teams.map((team, index) => (
                  <option key={team.id ?? index} value={team.id}>
                    {team.nameCountry}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <Input
            label="Čas zápasu"
            onChange={handleMatchTime}
            type="datetime-local"
          />

          <div className="flex gap-3 pt-1">
            <Btn type="button" variant="ghost" size="sm" fullWidth onClick={close}>
              Zrušit
            </Btn>
            <Btn type="submit" variant="primary" size="sm" fullWidth icon={<AiFillSave />}>
              Uložit
            </Btn>
          </div>
        </form>
      </div>
    </div>
  )
}
